// src/components/admin/RefundManagement.js
import React, { useState, useEffect, useMemo } from 'react';
import refundService from '../../services/refundService';
import LoadingSpinner from '../common/LoadingSpinner';
import { orgTimestamp } from '../../utils/dateUtils';
import './RefundManagement.css';

const RefundManagement = () => {
    const [refunds, setRefunds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending'); // pending, processing, disbursed, rejected, all
    const [selectedRefund, setSelectedRefund] = useState(null);
    const [showProcessModal, setShowProcessModal] = useState(false);
    const [showPrintPreviewModal, setShowPrintPreviewModal] = useState(false);
    const [printPreviewRefund, setPrintPreviewRefund] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [processData, setProcessData] = useState({
        status: 'processing',
        notes: '',
        refundMethod: 'bank_transfer',
        refundReference: ''
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const normalizeStatus = (status) => {
        if (status === 'approved') return 'processing';
        if (status === 'processed') return 'disbursed';
        return status;
    };

    const toApiStatus = (status) => {
        if (status === 'processing') return 'approved';
        if (status === 'disbursed') return 'processed';
        return status;
    };

    useEffect(() => {
        loadRefunds();
    }, [filter]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filter, searchQuery, rowsPerPage]);

    // Prevent background page scrolling while refund process modal is open.
    useEffect(() => {
        if (!showProcessModal && !showPrintPreviewModal) return undefined;
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [showProcessModal, showPrintPreviewModal]);

    const loadRefunds = async () => {
        try {
            setLoading(true);
            const filterValue = filter === 'all' ? null : toApiStatus(filter);
            const data = await refundService.getRefundRequests(filterValue);
            const normalizedRefunds = (data.refunds || []).map((refund) => ({
                ...refund,
                status: normalizeStatus(refund.status)
            }));
            setRefunds(normalizedRefunds);
        } catch (error) {
            console.error('Failed to load refunds:', error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenProcessModal = (refund) => {
        setSelectedRefund(refund);
        // Set default status based on current refund status
        const defaultStatus = refund.status === 'processing' ? 'disbursed' : 'processing';
        setProcessData({
            status: defaultStatus,
            notes: '',
            refundMethod: 'bank_transfer',
            refundReference: ''
        });
        setShowProcessModal(true);
    };

    const handleProcessRefund = async (e) => {
        e.preventDefault();

        if (!selectedRefund) return;

        if (processData.status !== 'rejected') {
            if (!processData.refundMethod) {
                alert('Please select a refund method');
                return;
            }
        }

        try {
            setProcessing(true);
            await refundService.processRefund(
                selectedRefund.id,
                toApiStatus(processData.status),
                processData.notes,
                processData.refundMethod,
                processData.refundReference || null
            );

            alert(`Refund ${processData.status} successfully!\nEmail notification sent to the organization.`);
            setShowProcessModal(false);
            setSelectedRefund(null);
            loadRefunds();
        } catch (error) {
            console.error('Failed to process refund:', error);
            alert(error.message || 'Failed to process refund');
        } finally {
            setProcessing(false);
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            // Some currencies (e.g. PKR) default to 0 fraction digits in Intl's
            // CLDR data even though this app always bills in 2 decimal places -
            // force it explicitly so the figure isn't silently rounded.
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // refund.requested_at / refund.processed_at come back from the API as naive
    // "YYYY-MM-DD HH:MM:SS" UTC strings. Without a timezone suffix, new Date()
    // misreads them as local time, so the displayed time is off by the viewer's
    // UTC offset instead of showing the actual submission/approval time. Prefer
    // the backend's pre-converted `_formatted` field; fall back to proper
    // UTC-aware parsing otherwise.
    const formatRefundTimestamp = (record, field) => {
        if (!record) return 'N/A';
        const result = orgTimestamp(record, field, record.org_timezone);
        return result === '—' ? 'N/A' : result;
    };

    // Net deduction (fee minus tax returned) can be negative - the tax given
    // back on refund can exceed the processing fee, making the payout a
    // credit above the original refundable amount rather than a reduction.
    const formatSignedDeduction = (amount, currency = 'USD') => {
        const value = Number(amount) || 0;
        const formatted = formatCurrency(Math.abs(value), currency);
        return value < 0 ? `+ ${formatted} (credit)` : `- ${formatted}`;
    };

    const getStatusBadgeClass = (status) => {
        const classes = {
            pending: 'status-pending',
            processing: 'status-processing',
            rejected: 'status-rejected',
            disbursed: 'status-disbursed',
            cancelled: 'status-cancelled'
        };
        return classes[status] || 'status-default';
    };

    const escapeHtml = (value) => {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    };

    const buildPrintableRefundHtml = (refund) => {
        const status = String(refund.status || 'N/A').toUpperCase();
        const now = formatDate(new Date().toISOString());
        const refundAmount = formatCurrency(refund.refund_amount || 0, refund.currency);
        const deductionAmount = formatSignedDeduction(refund.deduction_amount, refund.currency);
        const originalAmount = formatCurrency(refund.original_amount || 0, refund.currency);
        const reason = escapeHtml(refund.reason || 'N/A');
        const notes = escapeHtml(refund.refund_notes || 'N/A');
        const processedBy = escapeHtml(refund.processed_by_name || 'N/A');
        const processedAt = escapeHtml(formatRefundTimestamp(refund, 'processed_at'));
        const requestedBy = escapeHtml(refund.requested_by_name || 'N/A');
        const requestedAt = escapeHtml(formatRefundTimestamp(refund, 'requested_at'));
        const refundMethod = escapeHtml(refund.refund_method || 'N/A');
        const refundReference = escapeHtml(refund.refund_reference || 'N/A');

        return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Refund #${escapeHtml(refund.id)} - Printable View</title>
  <style>
    body { font-family: Arial, sans-serif; color: #111827; margin: 0; background: #f3f4f6; }
    .page { max-width: 900px; margin: 24px auto; background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; }
    .header { padding: 20px 24px; background: #111827; color: #fff; display: flex; justify-content: space-between; align-items: center; }
    .header h1 { margin: 0; font-size: 20px; }
    .header small { color: #d1d5db; }
    .body { padding: 24px; }
    .status { display: inline-block; padding: 6px 12px; border-radius: 999px; background: #e5e7eb; font-weight: 700; font-size: 12px; letter-spacing: 0.04em; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 16px; }
    .card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px; background: #fff; }
    .card h3 { margin: 0 0 10px 0; font-size: 14px; color: #374151; text-transform: uppercase; letter-spacing: 0.04em; }
    .row { display: flex; justify-content: space-between; gap: 16px; padding: 7px 0; border-bottom: 1px solid #f3f4f6; font-size: 14px; }
    .row:last-child { border-bottom: none; }
    .label { color: #6b7280; }
    .value { font-weight: 600; text-align: right; word-break: break-word; }
    .full { grid-column: 1 / -1; }
    .actions { display: flex; justify-content: flex-end; gap: 10px; margin: 16px 0 0 0; }
    button { border: 1px solid #d1d5db; background: #fff; border-radius: 6px; padding: 10px 14px; cursor: pointer; font-weight: 600; }
    button.primary { background: #111827; color: #fff; border-color: #111827; }
    .footer { padding: 14px 24px 24px 24px; color: #6b7280; font-size: 12px; }
    @media print {
      body { background: #fff; }
      .page { margin: 0; border: none; border-radius: 0; }
      .actions { display: none; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div>
        <h1>Refund Report #${escapeHtml(refund.id)}</h1>
        <small>Generated: ${escapeHtml(now)}</small>
      </div>
      <div class="status">${escapeHtml(status)}</div>
    </div>
    <div class="body">
      <div class="grid">
        <div class="card">
          <h3>Organization</h3>
          <div class="row"><span class="label">Name</span><span class="value">${escapeHtml(refund.organization_name || 'N/A')}</span></div>
          <div class="row"><span class="label">Organization ID</span><span class="value">${escapeHtml(refund.organization_id || 'N/A')}</span></div>
          <div class="row"><span class="label">Staff Count</span><span class="value">${escapeHtml(refund.staff_count || 'N/A')}</span></div>
          <div class="row"><span class="label">Months Refunded</span><span class="value">${escapeHtml(refund.months_refunded || 'N/A')}</span></div>
        </div>
        <div class="card">
          <h3>Amount Breakdown</h3>
          <div class="row"><span class="label">Original Amount</span><span class="value">${escapeHtml(originalAmount)}</span></div>
          <div class="row"><span class="label">Deduction</span><span class="value">${escapeHtml(deductionAmount)}</span></div>
          <div class="row"><span class="label">Deduction Percentage</span><span class="value">${escapeHtml(refund.deduction_percentage || 'N/A')}%</span></div>
          <div class="row"><span class="label">Refund Amount</span><span class="value">${escapeHtml(refundAmount)}</span></div>
        </div>
        <div class="card">
          <h3>Request Audit</h3>
          <div class="row"><span class="label">Requested By</span><span class="value">${requestedBy}</span></div>
          <div class="row"><span class="label">Requested At</span><span class="value">${requestedAt}</span></div>
          <div class="row"><span class="label">Processed By</span><span class="value">${processedBy}</span></div>
          <div class="row"><span class="label">Processed At</span><span class="value">${processedAt}</span></div>
        </div>
        <div class="card">
          <h3>Disbursement</h3>
          <div class="row"><span class="label">Method</span><span class="value">${refundMethod}</span></div>
          <div class="row"><span class="label">Reference</span><span class="value">${refundReference}</span></div>
        </div>
        <div class="card full">
          <h3>Reason</h3>
          <div style="font-size:14px; color:#374151; line-height:1.5;">${reason}</div>
        </div>
        <div class="card full">
          <h3>Admin Notes</h3>
          <div style="font-size:14px; color:#374151; line-height:1.5;">${notes}</div>
        </div>
      </div>
      <div class="actions">
        <button onclick="window.close()">Close</button>
        <button class="primary" onclick="window.print()">Print</button>
      </div>
    </div>
    <div class="footer">
      This document was generated from Refund Management and is intended for operational/audit use.
    </div>
  </div>
</body>
</html>
        `;
    };

    const openPrintableRefundView = (refund) => {
        setPrintPreviewRefund(refund);
        setShowPrintPreviewModal(true);
    };

    const printRefundPreview = () => {
        if (!printPreviewRefund) return;
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow?.document;
        if (!doc) {
            document.body.removeChild(iframe);
            alert('Unable to initialize print preview.');
            return;
        }

        doc.open();
        doc.write(buildPrintableRefundHtml(printPreviewRefund));
        doc.close();

        setTimeout(() => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        }, 250);
    };

    const filteredRefunds = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return refunds;

        return refunds.filter((refund) => {
            const searchable = [
                refund.id,
                refund.organization_name,
                refund.organization_id,
                refund.requested_by_name,
                refund.status,
                refund.refund_reference,
                refund.reason
            ];
            return searchable.some((value) => String(value ?? '').toLowerCase().includes(query));
        });
    }, [refunds, searchQuery]);

    const totalPages = Math.max(1, Math.ceil(filteredRefunds.length / rowsPerPage));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const paginatedRefunds = useMemo(() => {
        const start = (safeCurrentPage - 1) * rowsPerPage;
        return filteredRefunds.slice(start, start + rowsPerPage);
    }, [filteredRefunds, safeCurrentPage, rowsPerPage]);

    const resultStart = filteredRefunds.length === 0 ? 0 : ((safeCurrentPage - 1) * rowsPerPage) + 1;
    const resultEnd = Math.min(safeCurrentPage * rowsPerPage, filteredRefunds.length);

    return (
        <div className="refund-management">
            <div className="page-header">
                <div>
                    <h1><i className="fas fa-undo"></i> Refund Management</h1>
                    <p>Review and process refund requests from organizations</p>
                </div>
                <button className="btn btn-secondary" onClick={loadRefunds}>
                    <i className="fas fa-sync-alt"></i>
                    Refresh
                </button>
            </div>

            {/* Filter Tabs */}
            <div className="filter-tabs">
                <button
                    className={`tab ${filter === 'all' ? 'active' : ''}`}
                    onClick={() => setFilter('all')}
                >
                    All Requests
                </button>
                <button
                    className={`tab ${filter === 'pending' ? 'active' : ''}`}
                    onClick={() => setFilter('pending')}
                >
                    <i className="fas fa-clock"></i>
                    Pending
                </button>
                <button
                    className={`tab ${filter === 'processing' ? 'active' : ''}`}
                    onClick={() => setFilter('processing')}
                >
                    <i className="fas fa-spinner"></i>
                    Processing
                </button>
                <button
                    className={`tab ${filter === 'disbursed' ? 'active' : ''}`}
                    onClick={() => setFilter('disbursed')}
                >
                    <i className="fas fa-check-double"></i>
                    Disbursed
                </button>
                <button
                    className={`tab ${filter === 'rejected' ? 'active' : ''}`}
                    onClick={() => setFilter('rejected')}
                >
                    <i className="fas fa-times"></i>
                    Rejected
                </button>
            </div>

            {!loading && refunds.length > 0 && (
                <div className="refunds-toolbar">
                    <div className="refund-search-wrap">
                        <i className="fas fa-search"></i>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by org, requester, refund ID, status..."
                        />
                    </div>
                    <div className="refund-toolbar-right">
                        <label htmlFor="rowsPerPage">Rows:</label>
                        <select
                            id="rowsPerPage"
                            value={rowsPerPage}
                            onChange={(e) => setRowsPerPage(Number(e.target.value))}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                        <span className="refund-count">
                            {filteredRefunds.length} result{filteredRefunds.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                </div>
            )}

            {loading ? (
                <LoadingSpinner message="Loading refund requests..." />
            ) : refunds.length === 0 ? (
                <div className="empty-state">
                    <i className="fas fa-inbox"></i>
                    <h3>No Refund Requests</h3>
                    <p>There are no {filter !== 'all' ? filter : ''} refund requests at the moment.</p>
                </div>
            ) : filteredRefunds.length === 0 ? (
                <div className="empty-state">
                    <i className="fas fa-search"></i>
                    <h3>No Matching Results</h3>
                    <p>No refund requests matched your search criteria.</p>
                </div>
            ) : (
                <div className="refunds-table-container">
                    <table className="refunds-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Organization</th>
                                <th>Subscription Details</th>
                                <th>Refund Amount</th>
                                <th>Deduction</th>
                                <th>Status</th>
                                <th>Requested By</th>
                                <th>Requested At</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedRefunds.map((refund) => (
                                <tr key={refund.id}>
                                    <td>#{refund.id}</td>
                                    <td>
                                        <strong>{refund.organization_name}</strong>
                                        <br />
                                        <small>ID: {refund.organization_id}</small>
                                    </td>
                                    <td>
                                        {refund.staff_count} Staff
                                        <br />
                                        <small>{refund.months_refunded} month(s)</small>
                                    </td>
                                    <td>
                                        <strong className="refund-amount">
                                            {formatCurrency(refund.refund_amount, refund.currency)}
                                        </strong>
                                    </td>
                                    <td>
                                        <span className="deduction-amount">
                                            {formatSignedDeduction(refund.deduction_amount, refund.currency)}
                                        </span>
                                        <br />
                                        <small>({refund.deduction_percentage}%)</small>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${getStatusBadgeClass(refund.status)}`}>
                                            {refund.status.toUpperCase()}
                                        </span>
                                    </td>
                                    <td>
                                        {refund.requested_by_name}
                                    </td>
                                    <td>{formatRefundTimestamp(refund, 'requested_at')}</td>
                                    <td>
                                        {(refund.status === 'pending' || refund.status === 'processing') ? (
                                            <button
                                                className="btn btn-sm btn-primary"
                                                onClick={() => handleOpenProcessModal(refund)}
                                            >
                                                <i className="fas fa-edit"></i>
                                                Process
                                            </button>
                                        ) : (
                                            <button
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => {
                                                    setSelectedRefund(refund);
                                                    openPrintableRefundView(refund);
                                                }}
                                            >
                                                <i className="fas fa-eye"></i>
                                                View
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="refund-pagination">
                        <div className="refund-pagination-info">
                            Showing {resultStart}-{resultEnd} of {filteredRefunds.length}
                        </div>
                        <div className="refund-pagination-controls">
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                disabled={safeCurrentPage <= 1}
                            >
                                Previous
                            </button>
                            <span className="refund-page-indicator">
                                Page {safeCurrentPage} of {totalPages}
                            </span>
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                                disabled={safeCurrentPage >= totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Process Refund Modal */}
            {showProcessModal && selectedRefund && (
                <div className="modal-overlay" onClick={() => !processing && setShowProcessModal(false)}>
                    <div className="process-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Process Refund Request</h2>
                            <button
                                className="close-btn"
                                onClick={() => setShowProcessModal(false)}
                                disabled={processing}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="refund-summary">
                                <h3>Refund Details</h3>
                                <div className="summary-grid">
                                    <div className="summary-item">
                                        <label>Organization:</label>
                                        <span>{selectedRefund.organization_name}</span>
                                    </div>
                                    <div className="summary-item">
                                        <label>Staff Count:</label>
                                        <span>{selectedRefund.staff_count} Staff</span>
                                    </div>
                                    <div className="summary-item">
                                        <label>Months Refunded:</label>
                                        <span>{selectedRefund.months_refunded} Month(s)</span>
                                    </div>
                                    <div className="summary-item">
                                        <label>Original Amount:</label>
                                        <span>{formatCurrency(selectedRefund.original_amount, selectedRefund.currency)}</span>
                                    </div>
                                    <div className="summary-item">
                                        <label>Net Deduction:</label>
                                        <span className="deduction">
                                            {formatSignedDeduction(selectedRefund.deduction_amount, selectedRefund.currency)}
                                        </span>
                                    </div>
                                    <div className="summary-item highlight">
                                        <label>Refund Amount:</label>
                                        <strong className="refund-total">
                                            {formatCurrency(selectedRefund.refund_amount, selectedRefund.currency)}
                                        </strong>
                                    </div>
                                    <div className="summary-item full-width">
                                        <label>Reason:</label>
                                        <p className="reason-text">{selectedRefund.reason || 'No reason provided'}</p>
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={handleProcessRefund}>
                                <div className="form-group">
                                    <label htmlFor="status">Action <span className="required">*</span></label>
                                    <select
                                        id="status"
                                        value={processData.status}
                                        onChange={(e) => setProcessData({ ...processData, status: e.target.value })}
                                        required
                                        disabled={processing}
                                    >
                                        {selectedRefund.status === 'pending' && (
                                            <>
                                                <option value="processing">Mark as Processing</option>
                                                <option value="rejected">Reject</option>
                                            </>
                                        )}
                                        {selectedRefund.status === 'processing' && (
                                            <>
                                                <option value="disbursed">Mark as Disbursed</option>
                                                <option value="rejected">Reject</option>
                                            </>
                                        )}
                                    </select>
                                    <small>
                                        {processData.status === 'processing' && 'Move request to processing queue (email sent to client admin)'}
                                        {processData.status === 'disbursed' && 'Mark payment as disbursed & cancel subscription (email sent)'}
                                        {processData.status === 'rejected' && 'Reject the refund request (email sent)'}
                                    </small>
                                </div>

                                {processData.status !== 'rejected' && (
                                    <>
                                        <div className="form-group">
                                            <label htmlFor="refundMethod">
                                                Refund Method <span className="required">*</span>
                                            </label>
                                            <select
                                                id="refundMethod"
                                                value={processData.refundMethod}
                                                onChange={(e) => setProcessData({ ...processData, refundMethod: e.target.value })}
                                                required
                                                disabled={processing}
                                            >
                                                <option value="bank_transfer">Bank Transfer</option>
                                                <option value="original_gateway">Original Payment Gateway</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="refundReference">Refund Reference/Transaction ID</label>
                                            <input
                                                type="text"
                                                id="refundReference"
                                                value={processData.refundReference}
                                                onChange={(e) => setProcessData({ ...processData, refundReference: e.target.value })}
                                                placeholder="Enter transaction ID or reference number"
                                                disabled={processing}
                                            />
                                        </div>
                                    </>
                                )}

                                <div className="form-group">
                                    <label htmlFor="notes">Notes {processData.status === 'rejected' && '(Required for rejection)'}</label>
                                    <textarea
                                        id="notes"
                                        value={processData.notes}
                                        onChange={(e) => setProcessData({ ...processData, notes: e.target.value })}
                                        placeholder="Add any notes about this refund..."
                                        rows="4"
                                        disabled={processing}
                                        required={processData.status === 'rejected'}
                                    />
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowProcessModal(false)}
                                        disabled={processing}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className={`btn ${processData.status === 'rejected' ? 'btn-danger' : 'btn-primary'}`}
                                        disabled={processing}
                                    >
                                        {processing ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin"></i>
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <i className={`fas fa-${processData.status === 'rejected' ? 'times' : 'check'}`}></i>
                                                {processData.status === 'processing' && 'Mark Processing & Send Email'}
                                                {processData.status === 'disbursed' && 'Mark Disbursed & Send Email'}
                                                {processData.status === 'rejected' && 'Reject & Send Email'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {showPrintPreviewModal && printPreviewRefund && (
                <div className="modal-overlay" onClick={() => setShowPrintPreviewModal(false)}>
                    <div className="print-preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="print-preview-header">
                            <h2>Refund Report Preview #{printPreviewRefund.id}</h2>
                            <button
                                className="close-btn"
                                onClick={() => setShowPrintPreviewModal(false)}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="print-preview-body">
                            <div className="print-preview-grid">
                                <div className="preview-card">
                                    <h3>Organization</h3>
                                    <div className="preview-row"><span>Name</span><strong>{printPreviewRefund.organization_name || 'N/A'}</strong></div>
                                    <div className="preview-row"><span>Organization ID</span><strong>{printPreviewRefund.organization_id || 'N/A'}</strong></div>
                                    <div className="preview-row"><span>Staff Count</span><strong>{printPreviewRefund.staff_count || 'N/A'}</strong></div>
                                    <div className="preview-row"><span>Months Refunded</span><strong>{printPreviewRefund.months_refunded || 'N/A'}</strong></div>
                                </div>
                                <div className="preview-card">
                                    <h3>Amount Breakdown</h3>
                                    <div className="preview-row"><span>Original Amount</span><strong>{formatCurrency(printPreviewRefund.original_amount || 0, printPreviewRefund.currency)}</strong></div>
                                    <div className="preview-row"><span>Deduction</span><strong>{formatSignedDeduction(printPreviewRefund.deduction_amount, printPreviewRefund.currency)}</strong></div>
                                    <div className="preview-row"><span>Deduction Percentage</span><strong>{printPreviewRefund.deduction_percentage || 'N/A'}%</strong></div>
                                    <div className="preview-row"><span>Refund Amount</span><strong>{formatCurrency(printPreviewRefund.refund_amount || 0, printPreviewRefund.currency)}</strong></div>
                                </div>
                                <div className="preview-card">
                                    <h3>Audit Trail</h3>
                                    <div className="preview-row"><span>Status</span><strong>{String(printPreviewRefund.status || 'N/A').toUpperCase()}</strong></div>
                                    <div className="preview-row"><span>Requested By</span><strong>{printPreviewRefund.requested_by_name || 'N/A'}</strong></div>
                                    <div className="preview-row"><span>Requested At</span><strong>{formatRefundTimestamp(printPreviewRefund, 'requested_at')}</strong></div>
                                    <div className="preview-row"><span>Processed By</span><strong>{printPreviewRefund.processed_by_name || 'N/A'}</strong></div>
                                    <div className="preview-row"><span>Processed At</span><strong>{formatRefundTimestamp(printPreviewRefund, 'processed_at')}</strong></div>
                                </div>
                                <div className="preview-card">
                                    <h3>Disbursement</h3>
                                    <div className="preview-row"><span>Method</span><strong>{printPreviewRefund.refund_method || 'N/A'}</strong></div>
                                    <div className="preview-row"><span>Reference</span><strong>{printPreviewRefund.refund_reference || 'N/A'}</strong></div>
                                </div>
                                <div className="preview-card preview-card-full">
                                    <h3>Reason</h3>
                                    <p>{printPreviewRefund.reason || 'N/A'}</p>
                                </div>
                                <div className="preview-card preview-card-full">
                                    <h3>Admin Notes</h3>
                                    <p>{printPreviewRefund.refund_notes || 'N/A'}</p>
                                </div>
                            </div>
                        </div>

                        <div className="print-preview-footer">
                            <button className="btn btn-secondary" onClick={() => setShowPrintPreviewModal(false)}>
                                Close
                            </button>
                            <button className="btn btn-primary" onClick={printRefundPreview}>
                                <i className="fas fa-print"></i>
                                Print
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RefundManagement;
